import { getOr } from '@/utils/options';
import * as React from 'react';
import Button, { variantFromString } from 'components/src/button';
import { Heading, Label, Paragraph } from 'components/src/typography';
import { FormInputFieldsFragment, OrderPackageFormFieldsFragment } from 'types/generated';
import ISKFormat from '../common/ISKFormat';
import FormInput from './FormInput';
import Checkbox from '../../../../../packages/components/src/checkbox';
import QuantitySelect from '../common/QuantitySelect';
import clsx from 'clsx';
import { fullScreenChildGridClasses, gridGapClasses } from '@/styles/grid';
import ReCAPTCHA from 'react-google-recaptcha';
import HiddenInputFields from '../SalesForce/HiddenInputFields';
import { useAtom, useSetAtom } from 'jotai';
import typewriter from '@/analytics/segment';
import { addOnsQuantityAtom, isPackageFormOpenAtom } from '@/store';
import { AnimatePresence, motion } from 'framer-motion';
import Close from 'components/src/svg/Close';
import { colors } from 'components/src/foundation';
import ArrowLeft from 'components/src/svg/ArrowLeft';
import useIsMobile from '@/hooks/useIsMobile';
import { formatingPrice, getTotalPrice } from './PackageFormUtils';
import { AddOnWithQuantity } from '@/models/PackageTypes';

type FormProps = {
  data: OrderPackageFormFieldsFragment;
  hasAddons: boolean;
  addOns: Record<string, AddOnWithQuantity>;
  singleAddOns: AddOnWithQuantity[];
  multiAddOns: AddOnWithQuantity[];
  packageQuantity: number;
  formStep: number;
  packageDescription: string;
  setIsCartOpen: (isOpen: boolean) => void;
  setFormStep: (step: number) => void;
  handleCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const PackageForm = ({
  data,
  addOns,
  hasAddons,
  singleAddOns,
  multiAddOns,
  packageQuantity,
  formStep,
  packageDescription,
  handleCheckboxChange,
  setIsCartOpen,
  setFormStep,
}: FormProps) => {
  const recaptchaRef = React.createRef<ReCAPTCHA | null>();
  const isMobileSize = useIsMobile();
  const setAddOnsQuantity = useSetAtom(addOnsQuantityAtom);
  const [isFormVisible, setIsFormVisible] = useAtom(isPackageFormOpenAtom);

  const [verifiedTimestamp, setVerifiedTimestamp] = React.useState<string | null>(null);
  const [hasError, setHasError] = React.useState<boolean>(false);
  const [inputValues, setInputValues] = React.useState<Record<string, string>>(
    data.inputFieldsCollection?.items?.reduce<Record<string, string>>((acc, curr) => {
      // ID is required in Contentful, we need getOr here for type checking
      acc[getOr(curr?.id, 'NO_ID')] = '';
      return acc;
    }, {}) || {}
  );

  const submitDisabled = React.useMemo(() => {
    const hasEmptyFields = Object.values(inputValues).some((value) => !value.trim());
    return hasError || hasEmptyFields || !verifiedTimestamp;
  }, [inputValues, hasError, verifiedTimestamp]);

  const handleTextFieldChange = (inputField: FormInputFieldsFragment | null, value: string) => {
    setInputValues((prev) => ({ ...prev, [getOr(inputField?.id, 'NO_ID')]: value }));
  };

  const closeForm = () => {
    setIsFormVisible(false);
    setIsCartOpen(false);
    setFormStep(1);
  };

  React.useEffect(() => {
    if (isFormVisible) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [isFormVisible]);

  return (
    <AnimatePresence>
      {isFormVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={clsx(
            fullScreenChildGridClasses,
            'fixed left-1/2 top-1/2 z-[200] h-full w-full -translate-x-1/2 -translate-y-1/2  gap-y-16 backdrop-blur-md transition-filter'
          )}
        >
          <motion.form
            layout
            initial={{ opacity: 0, translateY: '20%' }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: '20%' }}
            className="red-bottom-shadow no-scrollbar relative col-span-full mx-16 my-auto grid max-h-[90%] max-w-screen-xl overflow-hidden overflow-y-scroll rounded-28 bg-white-50 lg:mx-auto"
            action={`https://${process.env.NEXT_PUBLIC_SF_ENDPOINT_PREFIX}.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=${process.env.NEXT_PUBLIC_SF_ORG_ID}`}
            method="POST"
            onSubmit={() => {
              if (recaptchaRef.current) {
                recaptchaRef.current.execute();
              }
              typewriter.packageFormSubmitted({
                package: getOr(data.title, 'Ekkert nafn á pakka'),
              });
            }}
          >
            <div className="flex items-center justify-between p-24 pb-0">
              <Button
                label={formStep === 1 ? 'Loka' : 'Til baka'}
                variant="text"
                labelClasses="label-sm"
                icon={
                  formStep === 1 ? (
                    <Close color={colors.neutral[10]} size="18" />
                  ) : (
                    <ArrowLeft color={colors.neutral[10]} />
                  )
                }
                onClick={() => {
                  formStep === 1 ? closeForm() : setFormStep(1);
                }}
              />
              {hasAddons && <Paragraph classes="flex px-24 md:hidden" variant="small">{`Skref ${formStep}`}</Paragraph>}
            </div>
            <HiddenInputFields verifiedTimestamp={verifiedTimestamp} isB2B={false} />
            <motion.div layout="position" className="col-span-full grid grid-cols-12">
              {formStep === 1 && (
                <div className={clsx('col-span-full grid gap-y-16 px-24 md:px-40', hasAddons && 'md:col-span-6')}>
                  <div className="md:py-24">
                    {data.subtitle && <Label classes="text-black-75">{data.subtitle}</Label>}
                    {data.title && (
                      <Heading variant="xs" classes="!font-bold">
                        {data.title}
                      </Heading>
                    )}
                  </div>
                  <div className={clsx(gridGapClasses, 'grid grid-cols-1 gap-y-8')}>
                    {data.inputFieldsCollection?.items?.map((formInput) => (
                      <FormInput
                        key={formInput?.id}
                        data={formInput as FormInputFieldsFragment}
                        setError={setHasError}
                        onChange={(value) => handleTextFieldChange(formInput, value)}
                      />
                    ))}
                  </div>
                  {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && (
                    <div className="md: flex justify-center md:justify-start">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                        onChange={() => setVerifiedTimestamp(JSON.stringify(new Date().getTime()))}
                        onExpired={() => setVerifiedTimestamp(null)}
                        onErrored={() => setVerifiedTimestamp(null)}
                      />
                    </div>
                  )}
                </div>
              )}
              {hasAddons && (formStep === 2 || !isMobileSize) && (
                <motion.div
                  layout="position"
                  className="col-span-full grid gap-y-16 rounded-28 p-24 md:col-span-6 md:mx-24 md:bg-white-75 lg:mx-40"
                >
                  {hasAddons && (
                    <Heading variant="xs" classes="label-lg flex items-end px-16 !font-bold text-red-100">
                      {'Viðbætur'}
                    </Heading>
                  )}
                  <div className={clsx(gridGapClasses, 'grid grid-cols-1 gap-y-16')}>
                    {singleAddOns.length > 0 && (
                      <div className="col-start-1 flex flex-col gap-16">
                        {singleAddOns.map(
                          ({ addOn }) =>
                            addOn.id && (
                              <Checkbox
                                key={addOn.id}
                                label={`${addOn.title} ${addOn.price ? formatingPrice(addOn.price) : ''}`}
                                id={addOn.id}
                                onChange={handleCheckboxChange}
                                checked={addOns[addOn.id]?.quantity > 0}
                                large
                              />
                            )
                        )}
                      </div>
                    )}
                    {multiAddOns.length > 0 && (
                      <div className="flex flex-col gap-16">
                        {multiAddOns.map(({ addOn, quantity }) => (
                          <div
                            className="flex flex-row items-center justify-between gap-8 p-16 md:flex-row"
                            key={addOn.id}
                          >
                            <Paragraph variant="label">{`${addOn.title} ${
                              addOn.price ? formatingPrice(addOn.price) : ''
                            }`}</Paragraph>
                            <QuantitySelect
                              value={quantity}
                              onChange={(changeValue) => {
                                if (addOn.id) {
                                  setAddOnsQuantity({ id: addOn.id, change: changeValue });
                                }
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
              <textarea hidden id="description" name="description" value={packageDescription} readOnly />
              <div className="col-span-full flex w-full grid-flow-col items-center justify-between gap-16 p-24 md:p-40 md:py-24">
                <div className="grid grid-flow-col items-baseline space-x-8 justify-self-center">
                  {data.packagePrice && (
                    <>
                      <ISKFormat
                        value={getTotalPrice(addOns, data.packagePrice || 0)}
                        renderText={(value) => (
                          <motion.div key={packageQuantity} initial={{ scale: 1.1 }} animate={{ scale: 1 }}>
                            <Heading classes="!font-bold" variant="xs">
                              {value}
                            </Heading>
                          </motion.div>
                        )}
                      />
                      <Paragraph>{'/mán'}</Paragraph>
                    </>
                  )}
                </div>
                <div className="flex flex-col items-center gap-16 lg:flex-row">
                  {hasAddons && data.button?.label && data.button?.variant && isMobileSize ? (
                    <>
                      {formStep === 1 && (
                        <Button
                          autoWidth={false}
                          label={'Áfram'}
                          variant="primary"
                          disabled={submitDisabled}
                          onClick={() => {
                            setFormStep(2);
                          }}
                        />
                      )}
                      {formStep === 2 && data.button?.label && data.button?.variant && (
                        <Button
                          type="submit"
                          autoWidth={false}
                          label={getOr(data.button.label, '')}
                          variant={variantFromString(data.button.variant)}
                          disabled={submitDisabled}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {data.button?.label && data.button?.variant && (
                        <Button
                          type="submit"
                          autoWidth={false}
                          label={getOr(data.button.label, '')}
                          variant={variantFromString(data.button.variant)}
                          disabled={submitDisabled}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PackageForm;
